/**
 * Server mode: the inventory and container screens over the server's stacks.
 *
 * The server owns every stack (server/actions/inventory.ts). This keeps a
 * copy of what it said (`truth`), shows it in the existing screens, and turns
 * what the player does in them into `click` intents:
 *
 *  - A screen applies a click locally with game/menu.ts (the prediction) and
 *    reports it through `ScreenDeps.remote`; the server runs the same function
 *    and answers with its `slots` / `cursor` diff, then `clicked {seq}`.
 *  - While a click is unanswered the screen keeps the prediction; once the last
 *    one is answered the screen is set to the truth, which undoes a wrong guess.
 *  - `open` builds a local stand-in for the container (a FurnaceState the
 *    server's `prop`s drive, a chest's stacks) and opens the matching screen;
 *    closing it sends `close`.
 *  - Outside the screens, a slot that disagrees with the server for longer than
 *    a click takes is set to the truth in survival, and pushed to the server
 *    in creative (where the client decides, as in vanilla).
 *
 * Window 0 on the wire: 0..35 main, 36..39 armour, 40 offhand, 41 the 2x2
 * result, 42..45 the 2x2 grid. Click slots are game/menu.ts menu indices.
 */

import type { Inventory } from '../game/inventory';
import { clearStack, emptyStack, isEmpty, setStack, type Stack } from '../game/types';
import { BTN_OFFHAND, CLICK, SLOT_OUTSIDE, type ClickArgs } from '../game/menu';
import { FurnaceState } from '../game/furnace';
import { BrewingStandState } from '../game/brewing/stand';
import { countBookshelves } from '../game/enchant/table';
import type { ScreenHost } from '../ui/screens/screenHost';
import type { ContainerScreen } from '../ui/screens/container';
import type { ServerSession } from './serverSession';

type Wire = Stack | null;
interface OpenMsg { win: number; kind: string; title?: string; size?: number; x?: number; y?: number; z?: number }

/** An open container window: the server's own slots, its props and the screen showing it. */
export interface ClientWindow {
  id: number;
  kind: string;
  own: Stack[];
  props: Record<string, number>;
  screen: ContainerScreen | null;
  /** A prop changed (stand-in state follows it). */
  onProp?: (k: string, v: number) => void;
}

export interface WindowsDeps {
  srv: ServerSession;
  screens: ScreenHost;
  inventory: Inventory;
  getBlock(x: number, y: number, z: number): number;
  level(): number;
  /** Sound of an anvil / grindstone use. */
  useSound?(): void;
}

const CORE = 41, ALL = 46;
/** How long a prediction (or a local change the server did not make) may stand unanswered. */
const SETTLE_MS = 1500;

const put = (dst: Stack, s: Wire | Stack): void => { if (!s || s.count <= 0 || s.id === 0) clearStack(dst); else setStack(dst, s); };
const eq = (a: Stack, b: Stack): boolean => {
  const ea = isEmpty(a), eb = isEmpty(b);
  if (ea || eb) return ea === eb;
  return a.id === b.id && a.count === b.count && (a.dmg ?? 0) === (b.dmg ?? 0) && (a.name ?? '') === (b.name ?? '')
    && JSON.stringify(a.ench ?? []) === JSON.stringify(b.ench ?? []);
};
const wire = (s: Stack): Wire => (isEmpty(s) ? null : { ...s, ench: s.ench?.map((e) => ({ ...e })) });

export class ClientWindows {
  /** What the server last said window 0 holds. */
  readonly truth: Stack[] = Array.from({ length: ALL }, emptyStack);
  readonly cursor: Stack = emptyStack();
  window: ClientWindow | null = null;
  /** Screens for window kinds other modules add (villager trades): return the screen it opened. */
  readonly openers: Record<string, (w: ClientWindow, m: OpenMsg) => void> = {};

  private seq = 0;
  private acked = 0;
  private sentAt = 0;
  private readonly since = new Float64Array(CORE);
  private lastSel = -1;
  private invOpen = false;
  private readonly local: Stack[];

  constructor(private readonly d: WindowsDeps) {
    const { srv, screens, inventory } = d;
    this.local = [...inventory.slots, ...inventory.armor, inventory.offhand];
    const w = srv.welcome;
    if (w) {
      w.inv.forEach((s, i) => { if (i < CORE) put(this.truth[i], s); });
      if (Number.isInteger(w.sel)) inventory.setSelected(w.sel);
      this.lastSel = inventory.selected;
    }
    this.applyAll();

    // the screens: clicks go to the server, which also does the dropping and pays the experience
    const deps = screens.deps;
    const changed = deps.onChange;
    deps.remote = (c) => { this.click(c); };
    deps.dropStack = (s) => { if (screens.creativeOpen) this.creativeSet(SLOT_OUTSIDE, s); };
    deps.addXp = () => { /* the server pays */ };
    deps.onChange = () => { changed(); if (deps.gameMode() === 'creative') this.pushCreative(); };
    this.changed = changed;

    srv.on('slots', (m) => {
      const msg = m as unknown as { win: number; s: [number, Wire][] };
      if (msg.win === 0) { for (const [i, s] of msg.s) if (this.truth[i]) put(this.truth[i], s); }
      else if (this.window && msg.win === this.window.id) for (const [i, s] of msg.s) put(this.window.own[i] ??= emptyStack(), s);
      if (this.settled()) this.applyAll();
    });
    srv.on('cursor', (m) => {
      put(this.cursor, (m as unknown as { s: Wire }).s);
      if (this.settled()) this.applyAll();
    });
    srv.on('clicked', (m) => {
      const seq = Number((m as unknown as { seq: number }).seq);
      if (seq > this.acked) this.acked = seq;
      if (this.settled()) this.applyAll();
    });
    srv.on('open', (m) => { this.onOpen(m as unknown as OpenMsg); });
    srv.on('closed', (m) => {
      const w0 = this.window;
      if (!w0 || w0.id !== (m as unknown as { win: number }).win) return;
      this.window = null;
      if (w0.screen && screens.screen === w0.screen) screens.close();
      this.applyAll();
    });
    srv.on('prop', (m) => {
      const p = m as unknown as { win: number; k: string; v: number };
      const w0 = this.window;
      if (!w0 || w0.id !== p.win) return;
      w0.props[p.k] = p.v;
      w0.onProp?.(p.k, p.v);
    });
  }

  private changed: () => void = () => { /* set in the constructor */ };

  /** No click is waiting for its answer (or the answer is overdue and the truth wins). */
  private settled(): boolean {
    return this.acked >= this.seq || performance.now() - this.sentAt > SETTLE_MS;
  }

  private sendClick(win: number, c: ClickArgs & { item?: Wire }): void {
    this.sentAt = performance.now();
    this.d.srv.send({ t: 'click', win, slot: c.slot, btn: c.btn, mode: c.mode, seq: ++this.seq, slots: c.slots ? [...c.slots] : undefined, text: c.text, grid: c.grid ? [...c.grid] : undefined, item: c.item });
  }

  /** A click a shared-menu screen just applied. */
  private click(c: ClickArgs): void {
    const w = this.window;
    this.sendClick(w && w.screen === this.d.screens.screen ? w.id : 0, c);
  }

  private creativeSet(slot: number, s: Stack): void {
    this.sendClick(0, { slot, btn: 0, mode: CLICK.CREATIVE_SET, item: wire(s) });
  }

  /** Creative: the client's inventory is the one that counts; tell the server every slot that differs. */
  private pushCreative(): void {
    for (let i = 0; i < CORE; i++) {
      if (eq(this.local[i], this.truth[i])) continue;
      put(this.truth[i], this.local[i]);
      this.creativeSet(i, this.local[i]);
    }
  }

  /** Show the server's stacks: inventory, the open screen's container slots and its cursor. */
  private applyAll(): void {
    const { screens } = this.d;
    for (let i = 0; i < CORE; i++) { put(this.local[i], this.truth[i]); this.since[i] = 0; }
    const screen = screens.screen;
    const menu = screen?.sharedMenu;
    if (screen && menu) {
      const w = this.window;
      if (w && w.screen === screen) {
        for (let i = 0; i < menu.own && i < w.own.length; i++) put(menu.slots[i].stack, w.own[i]);
      } else if (menu.kind === 'inventory') {
        for (let i = 0; i < 5; i++) put(menu.slots[i].stack, this.truth[CORE + i]);
      }
      put(screen.cursor, this.cursor);
      menu.recompute?.();
    }
    this.changed();
  }

  private onOpen(m: OpenMsg): void {
    const { screens } = this.d;
    const w: ClientWindow = { id: m.win, kind: m.kind, own: [], props: {}, screen: null };
    this.window = w;
    const noop = (): void => { /* the server does it */ };
    switch (m.kind) {
      case 'chest': {
        const size = m.size ?? 27;
        screens.openChest(Array.from({ length: size }, emptyStack), m.title ?? 'Chest');
        break;
      }
      case 'crafting': screens.openCraftingTable(); break;
      case 'furnace': {
        const f = new FurnaceState();
        w.onProp = (k, v) => { if (k === 'burnLeft') f.burnLeft = v; else if (k === 'burnTotal') f.burnTotal = v; else if (k === 'cook') f.cookProgress = v; };
        screens.openFurnace(f);
        break;
      }
      case 'brewing': {
        const s = new BrewingStandState();
        w.onProp = (k, v) => { if (k === 'brewTime') s.brewTime = v; else if (k === 'fuelLeft') s.fuelLeft = v; };
        screens.openBrewing(s);
        break;
      }
      case 'enchant': {
        const ctx = {
          bookshelves: m.x === undefined ? 0 : countBookshelves(this.d.getBlock, m.x, m.y ?? 0, m.z ?? 0),
          level: this.d.level, spendLevels: noop,
          seed: () => w.props.seed ?? 0, rerollSeed: noop,
        };
        w.onProp = (k, v) => { if (k === 'shelves') ctx.bookshelves = v; w.screen?.sharedMenu?.recompute?.(); };
        screens.openEnchanting(ctx);
        break;
      }
      case 'anvil': screens.openAnvil({ level: this.d.level, spendLevels: noop, onUse: this.d.useSound }); break;
      case 'grindstone': screens.openGrindstone({ addXp: noop, onUse: this.d.useSound }); break;
      case 'smithing': screens.openSmithing(); break;
      default: {
        const open = this.openers[m.kind];
        if (!open) return; // a window another module shows
        open(w, m);
      }
    }
    w.screen = screens.screen;
    this.applyAll();
  }

  /** A predicted use of the held stack was refused (client/blocks.ts): show the server's count again. */
  resync(): void { if (this.settled()) this.applyAll(); }

  /** Q: throw the held item (one, or the stack). */
  drop(all: boolean): void { this.d.srv.send({ t: 'drop', all }); }

  /** Swap main hand and offhand; the caller already swapped them locally. */
  swapOffhand(): void {
    this.sendClick(0, { slot: 36 + this.d.inventory.selected, btn: BTN_OFFHAND, mode: CLICK.SWAP });
  }

  /** Right-click on a container block: ask the server to open it. True when the click is handled here. */
  useBlock(x: number, y: number, z: number, face = 1): boolean {
    this.d.srv.send({ t: 'useOn', seq: ++this.useSeq, x, y, z, face, hx: 0.5, hy: 0.5, hz: 0.5, hand: 0 });
    return true;
  }
  private useSeq = 1 << 20;

  /** Once a frame. */
  update(): void {
    const { srv, screens, inventory } = this.d;
    if (inventory.selected !== this.lastSel) {
      this.lastSel = inventory.selected;
      srv.send({ t: 'held', slot: inventory.selected });
    }
    // a server window whose screen went away (Esc, E, another screen): tell the server
    const w = this.window;
    if (w && w.screen && screens.screen !== w.screen) {
      this.window = null;
      srv.send({ t: 'close', win: w.id });
    }
    // the inventory screen closed: its craft grid and cursor went back
    const invOpen = screens.inventoryOpen;
    if (this.invOpen && !invOpen && !this.window) srv.send({ t: 'close', win: 0 });
    this.invOpen = invOpen;

    if (!this.settled()) return;
    if (screens.deps.gameMode() === 'creative') { this.pushCreative(); return; }
    // survival: what the server did not do does not stand
    const now = performance.now();
    let fix = false;
    for (let i = 0; i < CORE; i++) {
      if (eq(this.local[i], this.truth[i])) { this.since[i] = 0; continue; }
      if (this.since[i] === 0) this.since[i] = now;
      else if (now - this.since[i] > SETTLE_MS) fix = true;
    }
    if (fix) this.applyAll();
  }
}

export function installWindows(d: WindowsDeps): ClientWindows {
  return new ClientWindows(d);
}
